/* global FileReader, document, window */
import * as React from "react";
import { createRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button } from "@fluentui/react-components";
import { clearCustomImage, restoreImage, setCustomImage } from "../../../state/cellStylesSlice";
import { InsertImage, Crop, Restore, Margins, BackArrow } from "../../../assets/icons";
import { onSelection } from "../../../helpers/selection";
import { openImageCropDialog } from "../../../state/appStateSlice";
import PropTypes from "prop-types";
import { Text, makeStyles } from "@fluentui/react-components";
import PaddingSubMenuItem from "./subMenuItems/PaddingSubMenuItem";
import { notify } from "../../providers/GlobalToast";
import { XIcon } from "@phosphor-icons/react";

const useClasses = makeStyles({
  PanelButton: {
    justifyContent: "left",
    fontFamily: "Arial",
    fontSize: 9,
    fontWeight: 400,
    letterSpacing: "0.02em",
  },
});

const ImageMenuItem = ({ isKeyboardSelected = false }) => {
  const classes = useClasses();
  const { t } = useTranslation();
  const [openPanelName, setOpenPanelName] = useState(null);

  const structure = useSelector((state) => state.gridd.present.structure);
  const selection = useSelector((state) => state.selection);
  const currentStyle = useSelector((state) => state.gridd.present.cellStyles[selection.cells[0]?.id]);
  const assets = useSelector((state) => state.gridd.present.cellStyles.assets);
  const dispatch = useDispatch();
  const fileInput = createRef();

  function getMenuButtons() {
    const mainMenuButtons = document.querySelectorAll('[id$="cellImageSelector"]');
    return mainMenuButtons;
  }

  useEffect(() => {
    if (!isKeyboardSelected) return;

    const menuButtons = getMenuButtons();
    let currentFocusIndex = 0;

    if (menuButtons.length > 0) {
      menuButtons[0].focus();
    }

    const handleKeyDown = (event) => {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          currentFocusIndex = (currentFocusIndex + 1) % menuButtons.length;
          menuButtons[currentFocusIndex].focus();
          break;

        case "ArrowUp":
          event.preventDefault();
          currentFocusIndex = (currentFocusIndex - 1 + menuButtons.length) % menuButtons.length;
          menuButtons[currentFocusIndex].focus();
          break;

        case "Enter":
          event.preventDefault();
          menuButtons[currentFocusIndex].click();
          break;

        case "F4":
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isKeyboardSelected]);

  return (
    <div style={{ border: isKeyboardSelected ? "2px solid #000000" : "none" }}>
      <input
        id="cellImageSelector"
        ref={fileInput}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          const reader = new FileReader();
          reader.readAsDataURL(e.target.files[0]);
          reader.onload = (ev) => {
            if (ev.total >= 128 * 1024) {
              notify("warning", "warning.image.tooLarge", undefined, {
                total: (ev.total / 1024).toFixed(),
              });
            }
            onSelection(
              dispatch,
              setCustomImage,
              { structure, selection },
              { id: selection.cells[0].id, img: { type: "base64", content: reader.result } }
            );
          };
        }}
      />
      <div style={{ display: openPanelName !== null ? "none" : "grid", gridTemplateRows: "auto auto auto auto" }}>
        <Button
          id="image_menu_insert_button"
          icon={<InsertImage />}
          appearance="subtle"
          className={classes.PanelButton}
          onClick={() => fileInput.current.click()}
        >
          {t(currentStyle?.img ? "menu.img.replace" : "menu.img.insert")}
        </Button>
        {currentStyle?.img && (
          <Button
            id="image_menu_crop_button"
            icon={<Crop />}
            appearance="subtle"
            className={classes.PanelButton}
            onClick={() =>
              dispatch(
                openImageCropDialog({
                  cellId: selection.cells[0]?.id,
                  imgContent: currentStyle.img.assetLink
                    ? assets[currentStyle.img.assetLink].content
                    : currentStyle.img.content,
                  crop: currentStyle.img.params?.crop,
                })
              )
            }
          >
            {t("menu.img.crop")}
          </Button>
        )}
        {currentStyle?.img && (
          <Button
            id="image_menu_home_margin_panel_button"
            icon={<Margins />}
            appearance="subtle"
            className={classes.PanelButton}
            onClick={() => setOpenPanelName("margin")}
          >
            {t("menu.image.margin")}
          </Button>
        )}
        {currentStyle?.img?.assetLink && (
          <Button
            id="image_menu_restore_button"
            icon={<Restore />}
            appearance="subtle"
            className={classes.PanelButton}
            onClick={() => dispatch(restoreImage({ id: selection.cells[0]?.id }))}
          >
            {t("menu.img.restore")}
          </Button>
        )}
        {currentStyle?.img && (
          <Button
            id="image_menu_remove_button"
            icon={<XIcon />}
            appearance="subtle"
            className={classes.PanelButton}
            onClick={() => onSelection(dispatch, clearCustomImage, { structure, selection })}
          >
            {t("menu.image.delete")}
          </Button>
        )}
      </div>
      <div style={{ display: openPanelName === "margin" ? "grid" : "none", gridTemplateRows: "auto, 1fr" }}>
        <div>
          <Button
            id="shape_menu_margin_back_button"
            icon={<BackArrow />}
            onClick={() => setOpenPanelName(null)}
            appearance="subtle"
            className={classes.BackArrow}
          />
          <Text className={classes.Header}>{t("menu.shape.margin")}</Text>
        </div>

        <PaddingSubMenuItem currentStyle={currentStyle} propertyName="margin" objectName="image" />
      </div>
    </div>
  );
};

ImageMenuItem.propTypes = {
  isKeyboardSelected: PropTypes.bool,
};

export default ImageMenuItem;
